import React from 'react';
import { format, parseISO } from 'date-fns';
import { WorkScheduleEvent, AvailabilityType } from './WorkScheduleCalendar';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface ViewEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: WorkScheduleEvent | null;
  onEdit: () => void;
  onDelete: () => void;
}

const ViewEventModal: React.FC<ViewEventModalProps> = ({
  isOpen,
  onClose,
  event,
  onEdit,
  onDelete,
}) => {
  useEscapeKey(onClose, isOpen);
  
  if (!isOpen || !event) return null;

  const getAvailabilityTypeLabel = (type: AvailabilityType) => {
    switch (type) {
      case 'AVAILABLE':
        return 'Available';
      case 'SURGERY_BLOCK':
        return 'Surgery Block';
      case 'UNAVAILABLE':
        return 'Unavailable';
      case 'EMERGENCY_ONLY':
        return 'Emergency Only';
      default:
        return type;
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />
      
      {/* Modal - Google Calendar Style */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
          {/* Header with Avatar and Icons */}
          <div className="relative px-6 pt-6 pb-4">
            {/* Top Icons Row */}
            <div className="absolute top-4 right-4 flex items-center space-x-2">
              {/* Delete */}
              <button
                onClick={onDelete}
                className="p-2 rounded-full hover:bg-red-50 transition-colors"
                title="Delete"
              >
                <svg className="w-5 h-5 text-gray-600 hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              
              {/* Close */}
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Avatar/Icon */}
            <div className="flex items-center space-x-3 mb-4 pr-24">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"/>
                </svg>
              </div>
            </div>

            {/* Title - Clickable to Edit */}
            <div 
              className="inline-flex items-center gap-2 px-2 py-1 -mx-2 rounded cursor-pointer hover:bg-gray-100 transition-colors group"
              onClick={onEdit}
              title="Click to edit title"
            >
              <h2 className="text-2xl font-normal text-gray-900">
                {event.title || getAvailabilityTypeLabel(event.availability_type)}
              </h2>
              {/* Edit icon - shows on hover */}
              <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            {/* Date/Time - Clickable to Edit */}
            <div 
              className="inline-flex items-center gap-2 mt-1 px-2 py-1 -mx-2 rounded cursor-pointer hover:bg-gray-100 transition-colors group"
              onClick={onEdit}
              title="Click to edit"
            >
              <p className="text-sm text-gray-600">
                {formatDateTime(event.start_at)}
              </p>
              {/* Edit icon - shows on hover */}
              <svg className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
          </div>

          {/* Content - Google Calendar Style */}
          <div className="px-6 pb-6 space-y-4">
            {/* Time - Clickable to Edit */}
            <div 
              className="flex items-start space-x-4 -mx-2 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors group"
              onClick={onEdit}
              title="Click to edit"
            >
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-900">
                    {formatTime(event.start_at)} – {formatTime(event.end_at)}
                  </p>
                  {(event.is_recurring || event.recurring_template_id) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {event.is_recurring ? 'Recurring event' : 'Part of recurring series'}
                    </p>
                  )}
                </div>
                {/* Edit icon - shows on hover */}
                <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>

            {/* Description/Notes */}
            {event.notes && (
              <div className="flex items-start space-x-4">
                <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{event.notes}</p>
                </div>
              </div>
            )}

            {/* Type */}
            <div className="flex items-start space-x-4">
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{getAvailabilityTypeLabel(event.availability_type)}</p>
              </div>
            </div>

            {/* Work Schedule */}
            <div className="flex items-start space-x-4">
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Work Schedule</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewEventModal;

